import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import type { User } from "../auth/api";

type Tool = {
  slug: string;
  path: string;
  name: string;
  desc: string;
  roles: Array<"admin" | "user" | "*">;
};

// Add new tools here as they come online.
const TOOLS: Tool[] = [
  {
    slug: "randes-search",
    path: "/randes-search",
    name: "Randes Search",
    desc: "EU trademark clearance — run a brand name through EUIPO and get a legal opinion.",
    roles: ["*"],
  },
];

function visibleTools(user: User | null): Tool[] {
  if (!user) return [];
  return TOOLS.filter(
    (t) => t.roles.includes("*") || t.roles.includes(user.role),
  );
}

export default function Landing() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If logged in and exactly one tool: go straight to it.
  useEffect(() => {
    if (!loading && user) {
      const tools = visibleTools(user);
      if (tools.length === 1) navigate(tools[0].path, { replace: true });
    }
  }, [loading, user, navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await login(email, password);
      // After login: if single tool, useEffect above will redirect.
      // If multiple tools, they'll see the tool list render below.
    } catch (err: any) {
      setError(
        err?.response?.data?.error === "invalid_credentials"
          ? "Wrong email or password."
          : "Sign-in failed. Try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="pt-10 pb-4 flex justify-center">
        <img src="/logo.png" alt="Maestro" className="h-10 w-auto" />
      </header>

      <main className="flex-1 max-w-lg w-full mx-auto px-6 pb-12">
        <p className="text-center font-display text-lg text-maestro-900 font-light mt-4 mb-10 tracking-wide">
          Home of Lars demos, experiments and tests.
        </p>

        {loading ? (
          <div className="bg-white border border-maestro-200 p-6 text-center text-maestro-400">
            Loading…
          </div>
        ) : user ? (
          <SessionCard user={user} />
        ) : (
          <form
            onSubmit={onSubmit}
            className="bg-white border border-maestro-200 p-7"
            noValidate
          >
            <h2 className="font-display text-sm uppercase tracking-[0.15em] text-maestro-900 mb-6">
              Sign in
            </h2>
            <label className="block mb-4">
              <span className="text-xs uppercase tracking-[0.1em] text-maestro-400">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                autoFocus
                className="mt-1 block w-full border border-maestro-200 px-3 py-2 text-sm text-maestro-900 focus:border-maestro-900 focus:outline-none"
              />
            </label>
            <label className="block mb-6">
              <span className="text-xs uppercase tracking-[0.1em] text-maestro-400">Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className="mt-1 block w-full border border-maestro-200 px-3 py-2 text-sm text-maestro-900 focus:border-maestro-900 focus:outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-maestro-900 hover:bg-black disabled:opacity-60 text-white py-2 font-display text-sm tracking-wider"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
            {error && (
              <p className="mt-3 text-red-600 text-sm">{error}</p>
            )}
          </form>
        )}
      </main>

      <footer className="px-4 py-4 text-[0.7rem] text-maestro-400 text-center tracking-widest uppercase">
        albinsson.ai
      </footer>
    </div>
  );
}

function SessionCard({ user }: { user: User }) {
  const { logout } = useAuth();
  const tools = visibleTools(user);

  return (
    <div className="bg-white border border-maestro-200 p-7">
      <div className="flex items-center justify-between mb-6">
        <span className="text-xs text-maestro-400 truncate pr-2">{user.email}</span>
        <button
          onClick={logout}
          className="font-display text-[0.7rem] uppercase tracking-[0.1em] text-maestro-700 hover:text-maestro-900"
        >
          Log out
        </button>
      </div>
      <h2 className="font-display text-sm uppercase tracking-[0.15em] text-maestro-900 mb-4">
        Your tools
      </h2>
      {tools.length === 0 ? (
        <p className="text-xs text-maestro-400">No tools available for your account.</p>
      ) : (
        <ul className="space-y-2">
          {tools.map((t) => (
            <li key={t.slug}>
              <Link
                to={t.path}
                className="block border border-maestro-200 hover:border-maestro-900 p-4 transition"
              >
                <div className="font-display font-semibold text-maestro-900">{t.name}</div>
                <div className="text-xs text-maestro-700 mt-1">{t.desc}</div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
